import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../store/authStore';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { CheckCircle, AlertTriangle, RefreshCw, Loader2, Filter, Timer, Settings } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export const SubmissionStatus = ({ qrId }) => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [retrying, setRetrying] = useState(null);
  const [autoRetry, setAutoRetry] = useState(null);
  const [savingRetry, setSavingRetry] = useState(false);

  useEffect(() => {
    fetchSubmissions();
    fetchAutoRetry();
  }, [qrId, filter]);

  const fetchSubmissions = async () => {
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await axios.get(`${API}/qr-codes/${qrId}/submission-status${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch {
      toast.error('Failed to load submission status');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (sessionId) => {
    setRetrying(sessionId);
    try {
      const res = await axios.post(
        `${API}/qr-codes/${qrId}/submission-status/${sessionId}/retry`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 }
      );
      if (res.data.submitted) {
        toast.success('Submission successful!');
      } else {
        toast.error(`Retry failed: ${res.data.detail}`);
      }
      fetchSubmissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Retry failed');
    } finally {
      setRetrying(null);
    }
  };

  const fetchAutoRetry = async () => {
    try {
      const res = await axios.get(`${API}/qr-codes/${qrId}/auto-retry`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAutoRetry(res.data);
    } catch {}
  };

  const toggleAutoRetry = async (enabled) => {
    setSavingRetry(true);
    try {
      const res = await axios.patch(
        `${API}/qr-codes/${qrId}/auto-retry`,
        { enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAutoRetry((prev) => ({ ...prev, ...res.data }));
      toast.success(enabled ? 'Auto-retry enabled' : 'Auto-retry disabled');
    } catch {
      toast.error('Failed to update auto-retry');
    } finally {
      setSavingRetry(false);
    }
  };

  const updateMaxRetries = async (val) => {
    try {
      const res = await axios.patch(
        `${API}/qr-codes/${qrId}/auto-retry`,
        { max_retries: val },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAutoRetry((prev) => ({ ...prev, ...res.data }));
    } catch {}
  };

  if (loading) {
    return (
      <Card className="p-6 border border-[#E2E8F0]">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-[#1d4ed8]" />
        </div>
      </Card>
    );
  }

  if (!data || data.total_completed === 0) {
    return (
      <div className="space-y-4">
        <Card className="p-6 border border-[#E2E8F0]" data-testid="submission-status-empty">
          <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Submission Status
          </h2>
          <p className="text-[#475569] text-sm">No completed submissions yet.</p>
        </Card>

        {autoRetry && (
          <Card className="p-4 border border-[#E2E8F0]" data-testid="auto-retry-settings-empty">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <Timer className="h-5 w-5 text-[#1d4ed8]" />
                <div>
                  <p className="font-bold text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Scheduled Auto-Retry
                  </p>
                  <p className="text-xs text-[#475569]">
                    {autoRetry.enabled ? 'Active — will auto-retry failed submissions' : 'Pre-enable to auto-retry failed submissions'}
                  </p>
                </div>
              </div>
              <Button
                data-testid="auto-retry-toggle-empty"
                size="sm"
                variant={autoRetry.enabled ? 'outline' : 'default'}
                onClick={() => toggleAutoRetry(!autoRetry.enabled)}
                disabled={savingRetry}
                className={`rounded-full text-xs ${!autoRetry.enabled ? 'bg-[#1d4ed8] hover:bg-[#1e40af]' : ''}`}
              >
                {savingRetry ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Settings className="h-3 w-3 mr-1" />}
                {autoRetry.enabled ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </Card>
        )}
      </div>
    );
  }

  const rate = data.submission_rate;
  const rateColor = rate >= 80 ? 'text-green-700 bg-green-50 border-green-200'
    : rate >= 50 ? 'text-amber-700 bg-amber-50 border-amber-200'
    : 'text-red-700 bg-red-50 border-red-200';

  return (
    <div className="space-y-4" data-testid="submission-status">
      {/* Summary Bar */}
      <Card className={`p-4 border ${rateColor}`} data-testid="submission-summary">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            {rate >= 80 ? (
              <CheckCircle className="h-5 w-5" />
            ) : (
              <AlertTriangle className="h-5 w-5" />
            )}
            <span className="font-bold text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {data.total_submitted}/{data.total_completed} submitted ({rate}%)
            </span>
          </div>
          {data.total_local > 0 && (
            <span className="text-sm">
              {data.total_local} stored locally
            </span>
          )}
        </div>
      </Card>

      {/* Auto-Retry Settings */}
      {autoRetry && (
        <Card className="p-4 border border-[#E2E8F0]" data-testid="auto-retry-settings">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Timer className="h-5 w-5 text-[#1d4ed8]" />
              <div>
                <p className="font-bold text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Scheduled Auto-Retry
                </p>
                <p className="text-xs text-[#475569]">
                  {autoRetry.enabled
                    ? `Active — ${autoRetry.pending_retries} pending · Max ${autoRetry.max_retries} attempts`
                    : 'Disabled — failed submissions won\'t be retried automatically'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {autoRetry.enabled && (
                <select
                  data-testid="max-retries-select"
                  value={autoRetry.max_retries}
                  onChange={(e) => updateMaxRetries(Number(e.target.value))}
                  className="text-xs border border-[#E2E8F0] rounded-lg px-2 py-1.5 bg-white"
                >
                  {[3, 5, 10, 15, 20].map((n) => (
                    <option key={n} value={n}>Max {n} retries</option>
                  ))}
                </select>
              )}
              <Button
                data-testid="auto-retry-toggle"
                size="sm"
                variant={autoRetry.enabled ? 'outline' : 'default'}
                onClick={() => toggleAutoRetry(!autoRetry.enabled)}
                disabled={savingRetry}
                className={`rounded-full text-xs ${!autoRetry.enabled ? 'bg-[#1d4ed8] hover:bg-[#1e40af]' : ''}`}
              >
                {savingRetry ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : (
                  <Settings className="h-3 w-3 mr-1" />
                )}
                {autoRetry.enabled ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Filter + Table */}
      <Card className="p-6 border border-[#E2E8F0]">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Submission Details
          </h2>
          <div className="flex items-center gap-1" data-testid="submission-filter">
            <Filter className="h-4 w-4 text-[#475569] mr-1" />
            {['all', 'submitted', 'local'].map((f) => (
              <Button
                key={f}
                size="sm"
                variant={filter === f ? 'default' : 'outline'}
                onClick={() => { setFilter(f); setLoading(true); }}
                className={`rounded-full text-xs ${filter === f ? 'bg-[#1d4ed8] hover:bg-[#1e40af]' : ''}`}
                data-testid={`filter-${f}`}
              >
                {f === 'all' ? 'All' : f === 'submitted' ? 'Submitted' : 'Local Only'}
              </Button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="submissions-table">
            <thead>
              <tr className="border-b border-[#E2E8F0]">
                <th className="text-left py-2 px-3 font-medium text-[#475569]">Status</th>
                <th className="text-left py-2 px-3 font-medium text-[#475569]">Completed</th>
                <th className="text-center py-2 px-3 font-medium text-[#475569]">Responses</th>
                <th className="text-left py-2 px-3 font-medium text-[#475569]">Method</th>
                <th className="text-left py-2 px-3 font-medium text-[#475569]">Detail</th>
                <th className="text-center py-2 px-3 font-medium text-[#475569]">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.submissions.map((s) => (
                <tr key={s.session_id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]">
                  <td className="py-2 px-3">
                    {s.submitted ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        <CheckCircle className="h-3 w-3" /> Submitted
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                        <AlertTriangle className="h-3 w-3" /> Local
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-[#475569]">{formatDate(s.completed_at)}</td>
                  <td className="py-2 px-3 text-center">{s.responses_count}</td>
                  <td className="py-2 px-3 text-[#475569] text-xs">{s.method}</td>
                  <td className="py-2 px-3 text-[#475569] text-xs max-w-[200px] truncate">{s.detail}</td>
                  <td className="py-2 px-3 text-center">
                    {!s.submitted && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRetry(s.session_id)}
                        disabled={retrying === s.session_id}
                        className="rounded-full text-xs"
                        data-testid={`retry-btn-${s.session_id}`}
                      >
                        {retrying === s.session_id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3 w-3" />
                        )}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {data.submissions.length === 0 && (
          <p className="text-sm text-[#475569] text-center py-4">
            No submissions match this filter.
          </p>
        )}
      </Card>
    </div>
  );
};
